import { useTraderCommissionStats } from '../../hooks/useTraderCommissionStats';
import { useTraderTime } from '../../contexts/TraderTimeContext';
import React, { useCallback, useState, useEffect } from 'react';
import { traderAPI, TraderSettings as TraderSettingsType } from '../../lib/api';
import { formatAmountWithSeparator } from '../../lib/utils';
import { balanceEvents, BALANCE_EVENTS } from '../../lib/events';
import { TRADER_BALANCE_REFRESH_INTERVAL_MS } from '../../lib/polling';
import { timezoneLabel } from '../../lib/traderTime';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Settings, DollarSign, AlertCircle, Loader2, Shield, TrendingUp } from 'lucide-react';
import { CommissionSummaryTable } from '../commissions/CommissionSummaryTable';

export const TraderSettings: React.FC = () => {
    const { formatDate, offsetMinutes, setOffsetMinutes, label } = useTraderTime();
    const [timezoneDraft, setTimezoneDraft] = useState(offsetMinutes);
    const [savingTimezone, setSavingTimezone] = useState(false);
    const [timezoneMessage, setTimezoneMessage] = useState('');
    const [timezoneError, setTimezoneError] = useState('');
    const saveTimezone = async () => {
        setSavingTimezone(true); setTimezoneMessage(''); setTimezoneError('');
        try {
            const result = await traderAPI.updateSettings({ timezone_offset_minutes: timezoneDraft });
            setOffsetMinutes(result.timezone_offset_minutes);
            setTimezoneMessage(`Часовой пояс сохранён: ${timezoneLabel(result.timezone_offset_minutes)}`);
        } catch {
            setTimezoneError('Не удалось сохранить часовой пояс. Попробуйте ещё раз.');
        } finally { setSavingTimezone(false); }
    };
    const timezoneOptions = Array.from(new Set([
        ...Array.from({ length: 27 }, (_, index) => (index - 12) * 60),
        -570, -210, 210, 270, 330, 345, 390, 525, 570, 630, 765, offsetMinutes,
    ])).sort((a, b) => a - b);
    const [settings, setSettings] = useState<TraderSettingsType | null>(null);
    const commissionStats = useTraderCommissionStats();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchSettings = useCallback(async (showLoading = false) => {
        try {
            if (showLoading) {
                setIsLoading(true);
            }
            setError(null);
            const data = await traderAPI.getSettings();
            setSettings(data);
        } catch (err: any) {
            setError('Ошибка загрузки настроек: ' + (err.response?.data?.detail || err.message));
        } finally {
            if (showLoading) {
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        fetchSettings(true);
    }, [fetchSettings]);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchSettings(false);
        }, TRADER_BALANCE_REFRESH_INTERVAL_MS);

        const handleBalanceUpdate = () => {
            fetchSettings(false);
        };

        balanceEvents.on(BALANCE_EVENTS.UPDATED, handleBalanceUpdate);
        balanceEvents.on(BALANCE_EVENTS.SETTINGS_CHANGED, handleBalanceUpdate);
        balanceEvents.on(BALANCE_EVENTS.PAYMENT_CONFIRMED, handleBalanceUpdate);
        balanceEvents.on(BALANCE_EVENTS.ORDER_CREATED, handleBalanceUpdate);
        balanceEvents.on(BALANCE_EVENTS.DEPOSIT_COMPLETED, handleBalanceUpdate);

        return () => {
            clearInterval(interval);
            balanceEvents.off(BALANCE_EVENTS.UPDATED, handleBalanceUpdate);
            balanceEvents.off(BALANCE_EVENTS.SETTINGS_CHANGED, handleBalanceUpdate);
            balanceEvents.off(BALANCE_EVENTS.PAYMENT_CONFIRMED, handleBalanceUpdate);
            balanceEvents.off(BALANCE_EVENTS.ORDER_CREATED, handleBalanceUpdate);
            balanceEvents.off(BALANCE_EVENTS.DEPOSIT_COMPLETED, handleBalanceUpdate);
        };
    }, [fetchSettings]);

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Settings className="h-5 w-5" />
                        Настройки трейдера
                    </CardTitle>
                </CardHeader>
                <CardContent className="flex justify-center items-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="ml-2">Загружаем настройки...</span>
                </CardContent>
            </Card>
        );
    }

    if (!settings) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Settings className="h-5 w-5" />
                        Настройки трейдера
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-300">
                        <AlertCircle className="h-5 w-5" />
                        <span>Не удалось загрузить настройки</span>
                    </div>
                    {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
                </CardContent>
            </Card>
        );
    }

    const fiatCurrency = settings.fiat_currency || 'RUB';
    const depositCommissionMethods = fiatCurrency === 'KGS'
        ? ['card', 'sbp', 'elqr']
        : ['card', 'sbp'];
    const withdrawCommissionMethods = ['card', 'sbp'];

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Настройки трейдера
                </CardTitle>
                <CardDescription>
                    Управление параметрами вашего трейдерского аккаунта
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="space-y-3">
                    <Label htmlFor="trader-timezone">Часовой пояс личного кабинета</Label>
                    <div className="flex flex-wrap gap-3">
                        <select id="trader-timezone" className="rounded-md border p-2 bg-white dark:bg-slate-800"
                            value={timezoneDraft} disabled={savingTimezone}
                            onChange={event => { setTimezoneDraft(Number(event.target.value)); setTimezoneMessage(''); setTimezoneError(''); }}>
                            {timezoneOptions.map(offset => <option key={offset} value={offset}>{timezoneLabel(offset)}</option>)}
                        </select>
                        <Button onClick={saveTimezone} disabled={savingTimezone || timezoneDraft === offsetMinutes}>
                            {savingTimezone ? 'Сохраняем…' : 'Сохранить часовой пояс'}
                        </Button>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-slate-400">
                        Дневной заработок, пуши, сделки, история, апелляции и фильтры дат рассчитываются и отображаются в выбранном часовом поясе на любом устройстве. Сейчас: {label}.
                    </p>
                    {timezoneMessage && <p role="status" className="text-sm text-green-600 dark:text-green-300">{timezoneMessage}</p>}
                    {timezoneError && <p role="alert" className="text-sm text-red-600 dark:text-red-300">{timezoneError}</p>}
                </div>
                {/* Информационные поля (только чтение) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-600 dark:text-slate-300">Баланс для обработки</Label>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-slate-900 rounded-md">
                                <DollarSign className="h-4 w-4 text-gray-500 dark:text-slate-400" />
                                <span className="font-medium">
                                    {formatAmountWithSeparator(settings.balance, 'USDT', 'ru-RU')}
                                </span>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                            Ваш текущий баланс для обработки заявок. Пополняйте через USDT TRC-20.
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-600 dark:text-slate-300">Последнее обновление</Label>
                        <div className="p-3 bg-gray-50 dark:bg-slate-900 rounded-md">
                            <span className="text-sm">
                                {formatDate(settings.updated_at)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Страховой баланс */}
                {settings.insurance_balance !== undefined && (
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-600 dark:text-slate-300">Страховой баланс</Label>
                        <div className="flex items-center gap-2 p-3 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-md">
                            <Shield className="h-4 w-4 text-orange-500" />
                            <span className="font-medium text-orange-700 dark:text-orange-300">
                                {formatAmountWithSeparator(settings.insurance_balance, 'USDT', 'ru-RU')}
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                            Страховой депозит, установленный администратором.
                        </p>
                    </div>
                )}

                {settings.trust_deposit !== undefined && settings.trust_deposit > 0 && (
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-600 dark:text-slate-300">Депозит доверия</Label>
                        <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                            <Shield className="h-4 w-4 text-blue-500" />
                            <span className="font-medium text-blue-700 dark:text-blue-300">
                                {formatAmountWithSeparator(settings.trust_deposit, 'USDT', 'ru-RU')}
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                            Дополнительный лимит на пополнение, установленный администратором.
                        </p>
                    </div>
                )}

                {/* Статистика комиссий */}
                <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-600 dark:text-slate-300 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Заработок с комиссий
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md">
                            <div className="text-xs text-green-600 dark:text-green-300 font-medium mb-1">Заработано сегодня</div>
                            <div className="text-xl font-bold text-green-700 dark:text-green-300">
                                {commissionStats ? formatAmountWithSeparator(commissionStats.today_commission_earned, 'USDT', 'ru-RU') : '—'}
                            </div>
                            <p className="text-xs text-green-500 mt-1">Новый день начинается в 00:00 по вашему времени</p>
                        </div>
                        <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                            <div className="text-xs text-blue-600 dark:text-blue-300 font-medium mb-1">За всё время</div>
                            <div className="text-xl font-bold text-blue-700 dark:text-blue-300">
                                {commissionStats ? formatAmountWithSeparator(commissionStats.total_commission_earned, 'USDT', 'ru-RU') : '—'}
                            </div>
                            <p className="text-xs text-blue-500 mt-1">Накоплено с начала работы</p>
                        </div>
                    </div>
                </div>

                {/* Блок баланса и комиссии */}
                <div className="space-y-6">
                    {/* Баланс с кнопкой пополнения */}
                    <div className="space-y-3">
                        <Label className="text-sm font-medium">Ваш баланс:</Label>
                        <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950 rounded-md">
                            <div>
                                <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                                    {formatAmountWithSeparator(settings.balance, 'USDT', 'ru-RU')}
                                </div>
                                <p className="text-sm text-green-600 dark:text-green-300">
                                    Доступно для обработки заявок
                                </p>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-slate-300">
                                Используйте кнопку "Пополнить баланс" в сайдбаре
                            </p>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                            Баланс пополняется через USDT TRC-20 депозиты. Сколько внесёте, столько и доступно.
                        </p>
                    </div>

                    {/* Комиссии за обработку платежей */}
                    <div className="space-y-3">
                        <Label className="text-sm font-medium">Ваши комиссии:</Label>

                        <CommissionSummaryTable
                            currency={fiatCurrency}
                            depositRate={settings.deposit_commission_rate}
                            withdrawRate={settings.withdraw_commission_rate}
                            depositTiers={settings.deposit_commission_tiers}
                            withdrawTiers={settings.withdraw_commission_tiers}
                            depositMethodTiers={settings.deposit_commission_tiers_by_requisite_type}
                            withdrawMethodTiers={settings.withdraw_commission_tiers_by_requisite_type}
                            depositMethods={depositCommissionMethods}
                            withdrawMethods={withdrawCommissionMethods}
                            methodLabel={(method) => {
                                if (method === 'card') return 'Карта';
                                if (method === 'elqr') return 'ElQR';
                                if (method === 'sbp') return fiatCurrency === 'KGS' ? 'Перевод по телефону (KGS)' : 'СБП';
                                return method;
                            }}
                        />

                        <p className="text-xs text-gray-500 dark:text-slate-400">
                            Комиссии начисляются автоматически при завершении платежей
                        </p>
                    </div>

                    {/* Сообщения об ошибках */}
                    {error && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-300 rounded-md">
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    {/* Кнопка обновления */}

                </div>
            </CardContent>
        </Card>
    );
};
